import { useTranslation } from 'react-i18next';

import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useDashboardOrganizationSummary } from '@/hooks/useDashboardOrganizationSummary';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { squareMetersToHectares } from '@/utils/plot';

export const useTotalSize = () => {
  const { t } = useTranslation();
  const { total_size: totalSize } = useDashboardOrganizationSummary().data ?? {};
  const formattedValue = useDisplayNumber(squareMetersToHectares(totalSize ?? 0));
  const formattedAreaValue = useDisplayNumber(squareMetersToHectares(totalSize ?? 0), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

  return {
    name: DefaultWidgetNames.default_total_size_card,
    type: WidgetType.o_card,
    visible: true,
    relatedData: {
      styles: {
        root: 'min-w-[200px] grow lg:basis-1/4 rounded-3xl',
        body: 'flex flex-col gap-4 p-8',
      },
    },
    widgets: [
      {
        name: 'total_size_header',
        type: WidgetType.a_text,
        visible: true,
        widgets: [
          {
            name: 'total_size_header_text',
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              i18nPath: 'global.widget.totalSize',
              className: 'typography-body1 text-text-primary',
            },
          },
          {
            name: 'total_size_header_tooltip',
            type: WidgetType.m_tooltip,
            visible: true,
            relatedData: {},
            widgets: [
              {
                name: 'total_size_header_tooltip_trigger',
                type: WidgetType.m_tooltip_trigger,
                visible: true,
                widgets: [
                  {
                    name: 'total_size_header_tooltip_trigger_icon',
                    type: WidgetType.a_icon,
                    visible: true,
                    relatedData: {
                      icon: 'ri-information-line',
                      className: 'ml-1 text-active-54',
                    },
                  },
                ],
              },
              {
                name: 'total_size_header_tooltip_content',
                type: WidgetType.m_tooltip_content,
                visible: true,
                widgets: [
                  {
                    name: 'total_size_header_tooltip_text',
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      text: `${t('global.widget.totalSizeTooltip')}\n\n${t('global.analysis.preciseValue', {
                        value: formattedAreaValue,
                      })}`,
                      className: 'whitespace-pre-wrap',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'total_size_value',
        type: WidgetType.a_text,
        visible: true,
        relatedData: {
          text: `${formattedValue} ha`,
          className: 'typography-h2 text-text-primary',
        },
      },
    ],
  } as WidgetSlotConfiguration;
};
